import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@0xtorch/evm'
import { createSolanaActionSource } from '@0xtorch/solana'
import { ApiSecretRequiredException } from '../../errors'
import type { FetchApiData } from '../../types'
import { networkEvmChainIds, solanaNetwork } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  config,
  from,
  createHeaders,
  proxy,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/api/v3/capital/deposit/hisrec',
    query: {
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // Sort by insertTime in ascending order
  const sortedDeposits = response.sort((a, b) => a.insertTime - b.insertTime)

  // Process data
  let stopped = false
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  for (const item of sortedDeposits) {
    // Skip deposits with status 7:REJECTED, 8:REFUND, 10:INVALID, 11:RESTRICTED
    if (
      item.status === 7 ||
      item.status === 8 ||
      item.status === 10 ||
      item.status === 11
    ) {
      continue
    }

    // Stop processing if deposit is pending (status 2:TIME_DELAY, 3:LARGE_DELAY, 4:PENDING, 6:AUDITING, 9:PRE_SUCCESS)
    if (
      item.status === 2 ||
      item.status === 3 ||
      item.status === 4 ||
      item.status === 6 ||
      item.status === 9
    ) {
      stopped = true
      break
    }

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined
        ? item.insertTime
        : Math.min(fetchedFrom, item.insertTime)
    fetchedTo =
      fetchedTo === undefined
        ? item.insertTime
        : Math.max(fetchedTo, item.insertTime)

    // Create EVM source if applicable
    if (item.network && item.txId) {
      const evmChainId = networkEvmChainIds[item.network]
      const txId = item.txId.split(':')[0] // Remove any potential network prefix
      if (evmChainId && isHex(txId)) {
        sources.push(
          createEvmActionSource({
            chainId: evmChainId,
            transactionHash: txId,
          }),
        )
      }
    }

    // Create Solana source if applicable
    if (item.network === solanaNetwork && item.txId) {
      sources.push(createSolanaActionSource({ signature: item.txId }))
    }
  }

  return {
    actions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped,
    to: fetchedTo,
  }
}
